'use client';

import { useTranslation } from 'next-i18next';
import { cn } from '@utils/cn';

interface SidebarContextSwitchProps {
  /** Which context is currently active. */
  activeContext: 'candidate' | 'company';
  /** Switches to the other context (same action as the account-menu row). */
  onSwitch: () => void;
  className?: string;
}

/**
 * Segmented context toggle (Caut job / Angajez) shown above the sidebar account row for
 * authenticated users. Same action as the switch row inside the account popup menu, but
 * always visible and showing both contexts.
 */
export const SidebarContextSwitch = ({ activeContext, onSwitch, className }: SidebarContextSwitchProps) => {
  const { t } = useTranslation('common');

  const segment = (context: 'candidate' | 'company', label: string) => {
    const isActive = activeContext === context;
    return (
      <button
        type="button"
        onClick={isActive ? undefined : onSwitch}
        aria-pressed={isActive}
        className={cn(
          'flex-1 truncate rounded-lg px-3 py-1.5 text-sm transition-all',
          isActive ? 'bg-surface font-semibold text-ink shadow-sm' : 'cursor-pointer font-medium text-ink-medium hover:text-ink',
        )}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      role="group"
      aria-label="Switch context"
      data-test-id="sidebar-switch-context"
      className={cn('flex w-full items-center gap-1 rounded-xl bg-surface-150 p-1', className)}
    >
      {segment('candidate', t('mc.switch.job.search'))}
      {segment('company', t('mc.switch.hire'))}
    </div>
  );
};
